// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useState, useEffect, type ChangeEvent, type FocusEvent } from 'react';
import { FormInput, type FormInputProps } from '@loa/ui-components';

type EuroFormInputProps = Readonly<
  Omit<FormInputProps, 'icon' | 'iconPosition' | 'type' | 'value' | 'onChange'> & {
    value: number | string | undefined;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    /** Hard cap — the input clamps to this value and shows an inline error when exceeded. */
    max?: number;
  }
>;

function formatThousands(raw: string): string {
  if (!raw) return '';
  const parts = raw.split('.');
  const intPart = (parts[0] || '0').replace(/^0+(?=\d)/, '') || '0';
  const formatted = intPart.replaceAll(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.length > 1 ? `${formatted}.${parts[1]}` : formatted;
}

function formatThousandsTwoDec(raw: string): string {
  if (!raw) return '';
  const n = Number.parseFloat(raw);
  if (!Number.isFinite(n)) return '';
  return formatThousands(n.toFixed(2));
}

function stripFormatting(display: string): string {
  const s = display.replaceAll(/[^0-9.]/g, '');
  const parts = s.split('.');
  return parts.length <= 1 ? s : parts[0] + '.' + parts.slice(1).join('');
}

function normalizeIncoming(value: number | string | undefined): string {
  if (value == null || value === '') return '';
  const n = typeof value === 'number' ? value : Number.parseFloat(String(value));
  if (!Number.isFinite(n) || n === 0) return '';
  return String(n);
}

export function EuroFormInput({
  value,
  onChange,
  max,
  className = '',
  onBlur,
  onFocus,
  error,
  ...rest
}: EuroFormInputProps) {
  const [text, setText] = useState<string>(() => normalizeIncoming(value));
  const [focused, setFocused] = useState(false);
  const [maxError, setMaxError] = useState<string | undefined>();

  // Resync from parent value when not actively editing (and numeric value actually changed)
  useEffect(() => {
    if (focused) return;
    const incoming = normalizeIncoming(value);
    setText((prev) => {
      const prevNum = prev ? Number.parseFloat(prev) : Number.NaN;
      const incNum = incoming ? Number.parseFloat(incoming) : Number.NaN;
      const sameNumeric =
        (Number.isNaN(prevNum) && Number.isNaN(incNum)) || prevNum === incNum;
      return sameNumeric ? prev : incoming;
    });
  }, [value, focused]);

  const emit = (e: ChangeEvent<HTMLInputElement>, raw: string) => {
    const synthetic = {
      ...e,
      target: { ...e.target, value: raw },
      currentTarget: { ...e.currentTarget, value: raw },
    } as ChangeEvent<HTMLInputElement>;
    onChange(synthetic);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = stripFormatting(e.target.value);
    if (raw && !/^\d*\.?\d*$/.test(raw)) return;

    if (max != null && raw) {
      const n = Number.parseFloat(raw);
      if (Number.isFinite(n) && n > max) {
        setMaxError(`Cannot exceed €${max.toLocaleString('en-IE')}`);
      } else {
        setMaxError(undefined);
      }
    } else {
      setMaxError(undefined);
    }
    setText(raw);
    emit(e, raw);
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    setFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    if (text) {
      const n = Number.parseFloat(text);
      if (Number.isFinite(n)) setText(n.toFixed(2));
    }
    onBlur?.(e);
  };

  const display = focused ? formatThousands(text) : formatThousandsTwoDec(text);

  return (
    <div className="relative">
      <FormInput
        {...rest}
        type="text"
        inputMode="decimal"
        value={display}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        error={error ?? maxError}
        className={`pl-xl ${className}`.trim()}
      />
      <span className="pointer-events-none absolute left-[16px] top-[24px] -translate-y-1/2 font-sans text-body-02 font-normal leading-normal text-black">
        €
      </span>
    </div>
  );
}
